<template>
  <div class="home">
    <HeroSection
      ref="heroSection"
      title="Tom Music"
      subtitle="Full Stack Developer"
      description="I build web applications, tools, and games. Welcome to my portfolio."
    />

    <div class="social-links" :class="{ fixed: isSocialLinksFixed }" :style="!isSocialLinksFixed ? { top: heroHeight + 'px' } : {}">
      <a
        href="https://github.com/Tomansion/"
        target="_blank"
        rel="noopener noreferrer"
        class="social-button github"
        title="GitHub"
      >
        <svg viewBox="0 0 24 24" fill="currentColor">
          <path
            d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v 3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"
          />
        </svg>
      </a>
      <a
        href="https://www.linkedin.com/in/tom-mansion/"
        target="_blank"
        rel="noopener noreferrer"
        class="social-button linkedin"
        title="LinkedIn"
      >
        <svg viewBox="0 0 24 24" fill="currentColor">
          <path
            d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.225 0z"
          />
        </svg>
      </a>
    </div>

    <main class="container">
      <CategorySection
        v-for="category in categories"
        :key="category"
        :title="category"
        :count="projectsByCategory[category]?.length || 0"
      >
        <ProjectCard
          v-for="project in projectsByCategory[category]"
          :key="project.slug"
          :project="project"
        />
      </CategorySection>
    </main>
  </div>
</template>

<script>
import HeroSection from "@/components/layout/Hero.vue";
import CategorySection from "@/components/layout/CategorySection.vue";
import ProjectCard from "@/components/common/ProjectCard.vue";
import { getAllProjects, getCategories } from "@/utils/projectsLoader";

export default {
  name: "HomePage",
  components: {
    HeroSection,
    CategorySection,
    ProjectCard,
  },
  data() {
    return {
      projects: [],
      categories: [],
      isSocialLinksFixed: false,
      heroHeight: 0,
    };
  },
  computed: {
    projectsByCategory() {
      const grouped = {};
      for (const project of this.projects) {
        if (!grouped[project.category]) {
          grouped[project.category] = [];
        }
        grouped[project.category].push(project);
      }
      return grouped;
    },
  },
  created() {
    this.projects = getAllProjects();
    this.categories = getCategories();
  },
  mounted() {
    // Get hero section height
    if (this.$refs.heroSection) {
      this.heroHeight = this.$refs.heroSection.$el.offsetHeight;
    }
    
    // Restore scroll position when returning to home
    window.scrollTo(0, 0);
    
    // Add scroll listener
    window.addEventListener("scroll", this.handleScroll);
  },
  beforeUnmount() {
    // Clean up scroll listener
    window.removeEventListener("scroll", this.handleScroll);
  },
  methods: {
    handleScroll() {
      this.isSocialLinksFixed = window.scrollY > this.heroHeight;
    },
  },
  beforeRouteLeave(to, from, next) {
    // Save scroll position before leaving
    sessionStorage.setItem("homeScrollPosition", window.scrollY);
    next();
  },
};
</script>

<style lang="scss" scoped>
.home {
  min-height: 100vh;
}

.social-links {
  position: absolute;
  padding: 1rem;
  right: 1rem;
  display: flex;
  justify-content: center;
  gap: 2rem;
  z-index: 1000;
  transition: position 0.3s ease;

  &.fixed {
    position: fixed;
    top: 1rem !important;
  }
}

.social-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background-color: #2d3748;
  color: white;
  transition: all 0.3s ease;
  text-decoration: none;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);

  svg {
    width: 24px;
    height: 24px;
  }

  &:hover {
    transform: translateY(-4px) scale(1.1);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.3);
  }

  &.github {
    &:hover {
      background-color: #1a202c;
      color: #ffffff;
    }
  }

  &.linkedin {
    &:hover {
      background-color: #0a66c2;
      color: #ffffff;
    }
  }
}

.empty-state {
  text-align: center;
  padding: 4rem 0;
  color: #64748b;
  font-size: 1.1rem;
}
</style>
